import BreakMinuteListOption from '@/models/bookings/breakMinuteListOption'
import DefaultBreakSelectionOption from '@/models/bookings/defaultBreakSelectionOption'
import calcTimeDifference from '@/utils/calc-time-difference'
import { minutesToHoursMinsString } from '@/utils/minutes-to-hours-mins-string'

/**
 * Generates a list of break minutes for the breaks select dropdown
 * @param {String} startTime 'HH:mm'
 * @param {String} endTime 'HH:mm'
 * @param {Number} incrementInMinutes
 * @param {Number} startIndex Indicates the index that the break mins list should be generated from. Default 0 (0 minutes)
 * @returns {Array} List of breaks
 * @notes Logic provided by BBB
 */
export default function generateBreakMinutesList(
  startTime,
  endTime,
  incrementInMinutes = 15,
  startIndex = 0
) {
  if (typeof incrementInMinutes !== 'number')
    throw Error('Invalid data type for increment (Expected Number)')

  if (typeof startIndex !== 'number')
    throw Error('Invalid data type for start index (Expected Number)')

  const bookingDurationInMins = calcTimeDifference(
    startTime,
    endTime,
    'minutes'
  )

  const breaks = []

  // Generate a list of options for the breaks select input
  // e.g. 0 minutes, 30 minutes etc..
  let minutes = 0
  for (
    let index = startIndex;
    index <= bookingDurationInMins / incrementInMinutes;
    ++index
  ) {
    minutes = index * incrementInMinutes
    breaks.push(generateBreakMinuteListOption(minutes))
  }

  return breaks
}

/**
 * Generates a break minute list option
 * @param {Number} minutes Number of minutes for the break duration
 */
export function generateBreakMinuteListOption(minutes) {
  return new BreakMinuteListOption(minutes, minutesToHoursMinsString(minutes))
}

/**
 * Generates a default break selection option based on the provided break minutes.
 * If the default break minutes are zero, it returns the first break option.
 * If the default break minutes fit exactly into the predefined increments and exist in the list,
 * it returns the corresponding break option.
 * Otherwise, it generates a new break option for the default break minutes and adjusts the index
 * to ensure it fits within the bounds of the breaks array, indicating that this new option should
 * be inserted into the list.
 *
 * @param {Array<BreakMinuteListOption>} breaks - The list of existing break options.
 * @param {number} defaultBreakMinutes - The default break duration in minutes.
 * @param {number} [breakIncrement=15] - The increment for break minutes. Default is 15.
 * @returns {DefaultBreakSelectionOption} - The generated default break selection option.
 */
export function generateDefaultBreakSelectionOption(
  breaks,
  defaultBreakMinutes,
  breakIncrement = 15
) {
  // If default minutes are 0, just use first generated break
  // which is 0 minutes
  if (defaultBreakMinutes === 0)
    return new DefaultBreakSelectionOption(breaks[0]) // Check if it's even possible for the default break minutes to be in // the list of options

  const isAcceptableBreakIndex = defaultBreakMinutes % breakIncrement === 0
  let breakIndex = Math.floor(defaultBreakMinutes / breakIncrement) // If the default break minutes fit exactly into the increments and // exists in the list, return it

  if (isAcceptableBreakIndex && breaks[breakIndex]) {
    return new DefaultBreakSelectionOption(breaks[breakIndex])
  } // Generate a new break option for the default break minutes

  const defaultBreakMins = generateBreakMinuteListOption(defaultBreakMinutes) // Adjust the index to ensure it fits within the bounds of the breaks array

  breakIndex = Math.min(breakIndex, breaks.length - 1) // Return new default break option and indicate it should be injected into the breaks list

  return new DefaultBreakSelectionOption(defaultBreakMins, true, breakIndex)
}
